import { serviceLabel, hirePhotoList, readingChange, targetRange, rangeStatus, targetText } from "./checksheets";
import { embedQuoteData } from "./quoteExport";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { photoDims } from "./photos";
import { today } from "./theme";

const BLUE  = [30, 95, 168];
const CYAN  = [43, 172, 209];
const GREEN = [34, 165, 90];
const GRAY  = [138, 138, 138];
const FAIL  = [208, 52, 44];

export function exportPDF(config, header, state, logoB64) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  let y = 0;

  const addPageIfNeeded = (needed = 20) => {
    if (y + needed > 278) { doc.addPage(); y = 15; }
  };

  const sectionHeading = (title, color = BLUE) => {
    addPageIfNeeded(14);
    doc.setFillColor(...color);
    doc.rect(10, y, W - 20, 7, "F");
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), 13, y + 5);
    y += 9;
  };

  const addPhoto = (src, px, py, maxW, maxH) => {
    try {
      const dims = photoDims.get(src);
      let pw = maxW, ph = maxH;
      if (dims && dims.w && dims.h) {
        const ratio = dims.w / dims.h;
        if (ratio > maxW / maxH) { pw = maxW; ph = maxW / ratio; }
        else                      { ph = maxH; pw = maxH * ratio; }
      }
      doc.addImage(src, "JPEG", px, py, pw, ph);
      return ph;
    } catch(e) { return maxH; }
  };

  // ── Header bar ──────────────────────────────────────────────────────────────
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, 28, "F");
  try { doc.addImage(logoB64, "PNG", 10, 4, 44, 19); } catch (e) {}
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text(`${serviceLabel(config)} Checksheet — ${config.equipment}`, W - 12, 10, { align: "right" });
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.setTextColor(200, 220, 255);
  doc.text("Specialist Utility Vehicles · waimea.co.nz", W - 12, 17, { align: "right" });
  doc.setFillColor(...CYAN);
  doc.rect(0, 28, W, 2, "F");
  y = 36;

  // ── Draft / placeholder warning — must carry through to the customer-facing
  // PDF, not just the on-screen banner, so a provisional checksheet is never
  // mistaken for a finished document once downloaded or sent out.
  if (config.draft) {
    const AMBER_BG = [254, 243, 199];
    const AMBER_BORDER = [245, 158, 11];
    const AMBER_TEXT = [146, 64, 14];
    doc.setFillColor(...AMBER_BG);
    doc.setDrawColor(...AMBER_BORDER);
    doc.roundedRect(10, y, W - 20, 10, 2, 2, "FD");
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...AMBER_TEXT);
    doc.text("⚠ DRAFT / PLACEHOLDER CHECKSHEET — provisional, pending review. Do not treat as final.", 14, y + 6.5);
    y += 14;
  }

  // ── Job info box ─────────────────────────────────────────────────────────────
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(10, y, W - 20, 22, 2, 2, "F");
  doc.setDrawColor(220, 228, 240);
  doc.roundedRect(10, y, W - 20, 22, 2, 2, "S");
  const jobFields = [
    ["Job No.", header.jobno || "—"],
    ["Date", header.date || today],
    ["Technician", header.tech || "—"],
    ...(config.service === "OH" || config.service === "PH" ? [] : [   // off-hire / pre-hire sheets have no customer/contact
      ["Customer", header.customer || "—"],
      ["Contact", header.contact || "—"],
    ]),
  ];
  const colW = (W - 20) / jobFields.length;
  jobFields.forEach((f, idx) => {
    const x = 12 + idx * colW;
    doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...BLUE);
    doc.text(f[0].toUpperCase(), x, y + 7);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(30, 30, 30);
    doc.text(String(f[1]), x, y + 14);
  });
  y += 27;

  // ── Equipment info ────────────────────────────────────────────────────────────
  const infoRows = [];
  if (header.rego)         infoRows.push(["Vehicle Rego", header.rego]);
  if (header.odo)          infoRows.push(["Odometer", `${header.odo} km`]);
  if (header.enghours)     infoRows.push(["Vehicle hrs", header.enghours]);
  if (header.pto)          infoRows.push(["PTO / unit hrs", header.pto]);
  if (header.nextsvc)      infoRows.push(["Next svc due", header.nextsvc]);
  if (header.make)         infoRows.push([config.equipment + " Make", header.make]);
  if (header.model)        infoRows.push([config.equipment + " Model", header.model]);
  if (header.serial)       infoRows.push([config.equipment + " Serial", header.serial]);
  if (header.tankerModel && header.tankerModel !== header.model) infoRows.push(["Tanker", header.tankerModel]);
  if (header.tankerSerial) infoRows.push(["Tanker Serial", header.tankerSerial]);
  if (state.completedAt)   infoRows.push(["Completed", new Date(state.completedAt).toLocaleString("en-NZ", { dateStyle:"medium", timeStyle:"short" })]);
  if (header.sheetId)      infoRows.push(["Sheet ID", header.sheetId]);

  if (infoRows.length) {
    const half = Math.ceil(infoRows.length / 2);
    const c1 = infoRows.slice(0, half), c2 = infoRows.slice(half);
    for (let i = 0; i < Math.max(c1.length, c2.length); i++) {
      addPageIfNeeded(8);
      const r1 = c1[i]||["",""], r2 = c2[i]||["",""];
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...GRAY);
      doc.text(r1[0].toUpperCase(), 12, y);
      doc.text(r2[0].toUpperCase(), W/2+4, y);
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(30,30,30);
      doc.text(r1[1]||"—", 12, y+4);
      doc.text(r2[1]||"—", W/2+4, y+4);
      y += 8;
    }
    y += 4;
  }

  // ── Certs ────────────────────────────────────────────────────────────────────
  if (config.certs?.length) {
    sectionHeading("Certs / Compliance");
    const certRows = config.certs.map(c => {
      const v = state.certs?.[c.id] || {};
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.expiry||"");
      return [c.label, v.val===true?"Pass":v.val===false?"Fail":v.val==="due"?"Due":"—", m ? `${m[3]}/${m[2]}/${m[1]}` : (v.expiry||"")];
    });
    autoTable(doc, {
      startY:y, head:[["Item","Result","Expiry"]], body:certRows,
      margin:{left:10,right:10},
      headStyles:{fillColor:CYAN,textColor:255,fontSize:8,fontStyle:"bold"},
      bodyStyles:{fontSize:8}, theme:"striped",
      alternateRowStyles:{fillColor:[248,250,253]},
      columnStyles:{1:{cellWidth:22,halign:"center"},2:{cellWidth:36}},
      didParseCell:d=>{
        if(d.section==="body"&&d.column.index===1){
          if(d.cell.raw==="Fail") { d.cell.styles.textColor=FAIL; d.cell.styles.fontStyle="bold"; }
          if(d.cell.raw==="Pass") d.cell.styles.textColor=GREEN;
          if(d.cell.raw==="Due") { d.cell.styles.textColor=[200,130,10]; d.cell.styles.fontStyle="bold"; }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
    // Photos of failed certs / labels
    const certPhotos = config.certs.filter(c => state.certs?.[c.id]?.val === false)   // fail photos only
      .flatMap(c => (state.certs?.[c.id]?.photos || []).map(src => ({ label: c.label, src })));
    if (certPhotos.length) {
      const pw = (W - 32) / 3, phh = pw * 0.75;
      certPhotos.forEach((p, i) => {
        const col = i % 3;
        if (col === 0) { addPageIfNeeded(phh + 12); }
        const px = 10 + col * (pw + 6);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...GRAY);
        doc.text(doc.splitTextToSize(p.label.toUpperCase(), pw)[0], px, y + 4);
        addPhoto(p.src, px, y + 6, pw, phh);
        if (col === 2 || i === certPhotos.length - 1) y += phh + 12;
      });
      y += 2;
    }
  }

  // ── Vehicle photos — after certs, before inspections ─────────────────────────
  const vp = state.vehiclePhotos || {};
  const vpSlots = [
    {key:"dashOn",        label:"Dash — ignition on"},
    {key:"front",         label:"Vehicle front"},
    {key:"driverSide",    label:"Driver side"},
    {key:"rear",          label:"Rear of vehicle"},
    {key:"passengerSide", label:"Passenger side"},
  ];
  // Two-column grid of labelled photos: entries = [{ label, src }]
  const photoGrid = (title, entries) => {
    const filled = entries.filter(e=>e.src);
    if (filled.length === 0) return;
    sectionHeading(title);
    const photoW = (W - 28) / 2;
    const photoH = photoW * 0.75;
    let col = 0;
    let rowY = y;
    filled.forEach(entry => {
      if (col === 0) {
        addPageIfNeeded(photoH + 14);
        rowY = y;
        y += photoH + 14;
      }
      const px = 10 + col * (photoW + 8);
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...GRAY);
      doc.text(entry.label.toUpperCase(), px, rowY + 4);
      addPhoto(entry.src, px, rowY + 6, photoW, photoH);
      col = col === 0 ? 1 : 0;
    });
    y += 4;
  };
  photoGrid("Vehicle Photos", vpSlots.map(s => ({ label: s.label, src: vp[s.key] })));
  // Condition photos — off-hire / pre-hire record of how the unit left us
  photoGrid("Condition Photos", (config.hirePhotos || []).flatMap(s => {
    const list = hirePhotoList(state, s.key);
    return list.map((src, i) => ({ label: list.length > 1 ? `${s.label} (${i + 1})` : s.label, src }));
  }));

  // ── Inspection sections ───────────────────────────────────────────────────────
  // ── Post-rebuild: which pre-rebuild it's compared with + what was found then ──
  const pre = config.comparesWith ? state.preRebuild : null;
  if (pre) {
    sectionHeading("Pre-rebuild comparison");
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
    const src = `Compared with pre-rebuild ${pre.jobno || "(no job no.)"}${pre.date ? `, ${pre.date}` : ""}${pre.tech ? `, by ${pre.tech}` : ""}. Cycle times and pump readings are judged against the targets from the manual; deflection, and any reading without a target, is flagged when it changed by more than 10%.`;
    const lines = doc.splitTextToSize(src, W - 20);
    addPageIfNeeded(lines.length * 4 + 4); doc.text(lines, 10, y); y += lines.length * 4 + 3;
    if (pre.defects?.length) {
      autoTable(doc, {
        startY:y, head:[["Defects found before the rebuild"]], body:pre.defects.map(d => [d]),
        margin:{left:10,right:10},
        headStyles:{fillColor:[120,130,145],textColor:255,fontSize:8,fontStyle:"bold"},
        bodyStyles:{fontSize:8,textColor:[90,90,90]}, theme:"striped", alternateRowStyles:{fillColor:[248,250,253]},
      });
      y = doc.lastAutoTable.finalY + 6;
    }
  }
  const statusText = s => s==="pass"?"✓ Pass":s==="na"?"N/A":s==="fail"?"✗ Fail":"—";

  config.sections.forEach(sec => {
    sectionHeading(sec.title);
    const beforeList = pre?.sections?.[sec.id] || null;
    const rows = sec.items.map((label,i) => {
      const st = state[sec.id]?.[i]||{};
      const note = [st.note, ...(st.extraFaults||[]).map(f=>f.note)].filter(Boolean).join("\n");  // extra faults listed under the first
      if (!beforeList) return [label, statusText(st.status), note];
      const b = beforeList.find(x => x.label === label) || beforeList[i];
      return [label, b ? statusText(b.status) : "—", statusText(st.status), note];
    });
    const statusCols = beforeList ? [1, 2] : [1];
    autoTable(doc, {
      startY:y, head:[beforeList ? ["Item","Before","After","Note"] : ["Item","Status","Note"]], body:rows,
      margin:{left:10,right:10},
      headStyles:{fillColor:CYAN,textColor:255,fontSize:8,fontStyle:"bold"},
      bodyStyles:{fontSize:8,textColor:[30,30,30]},
      columnStyles: beforeList
        ? {0:{cellWidth:"auto"},1:{cellWidth:18,halign:"center",textColor:[130,130,130]},2:{cellWidth:18,halign:"center"},3:{cellWidth:50}}
        : {0:{cellWidth:"auto"},1:{cellWidth:22,halign:"center"},2:{cellWidth:56}},
      theme:"striped", alternateRowStyles:{fillColor:[248,250,253]},
      didParseCell:d=>{
        if(d.section==="body"&&statusCols.includes(d.column.index)){
          if(d.cell.raw==="✗ Fail") d.cell.styles.textColor=FAIL;
          if(d.cell.raw==="✓ Pass") d.cell.styles.textColor=GREEN;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
    // Optional photos on items that didn't fail (fail photos print under Defects)
    {
      const ph = sec.items.flatMap((label, i) => { const st = state[sec.id]?.[i] || {}; return st.status !== "fail" ? (st.photos || []).map(src => ({ label, src })) : []; });
      if (ph.length) {
        const pw = (W - 32) / 3, phh = pw * 0.75;
        ph.forEach((p, i) => {
          const col = i % 3;
          if (col === 0) addPageIfNeeded(phh + 12);
          const px = 10 + col * (pw + 6);
          doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...GRAY);
          doc.text(doc.splitTextToSize(p.label.toUpperCase(), pw)[0], px, y + 4);
          addPhoto(p.src, px, y + 6, pw, phh);
          if (col === 2 || i === ph.length - 1) y += phh + 12;
        });
        y += 2;
      }
    }
    const sn = (state.sectionNotes?.[sec.id] || "").trim();
    if (sn) {
      const lines = doc.splitTextToSize(`Section notes: ${sn}`, W - 20);
      addPageIfNeeded(lines.length * 4 + 4);
      doc.setFontSize(8); doc.setFont("helvetica", "italic"); doc.setTextColor(60, 60, 60);
      doc.text(lines, 10, y); y += lines.length * 4 + 4;
    }
  });

  // ── Operational checks ────────────────────────────────────────────────────────
  if (config.operationalChecks) {
    sectionHeading("Operational Checks");
    const oc = state.operationalChecks || {};
    autoTable(doc, {
      startY:y, head:[["Check","Reading"]], body:[
        ["Blower — vacuum (inHg)",            oc.blowerVac||"—"],
        ["Blower — pressure (psi)",            oc.blowerPsi||"—"],
        ["Water pump — unloader relief (psi)", oc.wpUnloader||"—"],
      ],
      margin:{left:10,right:10},
      headStyles:{fillColor:CYAN,textColor:255,fontSize:8},
      bodyStyles:{fontSize:8}, theme:"striped",
      alternateRowStyles:{fillColor:[248,250,253]},
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Readings (pre-rebuild: cycle times, pump test, deflection) ───────────────
  (config.measurements || []).forEach(g => {
    sectionHeading(g.title);
    const mv = state.measures || {};
    const reading = (val, unit) => val==="na" ? "N/A" : (val ? `${val} ${unit}` : "—");
    // Cycle times: target range from the manual (the pre-rebuild's, else this sheet's) and the verdict
    const rangeFor = key => g.targets ? (targetRange(pre?.measures, key) || targetRange(mv, key)) : null;
    const target = (key, unit) => targetText(rangeFor(key), unit) || "—";
    const verdict = (key, unit) => {
      const s = rangeStatus(mv[key], rangeFor(key));
      return s === "ok" ? "In range" : s === "low" ? (unit === "sec" ? "TOO FAST" : "BELOW") : s === "high" ? (unit === "sec" ? "TOO SLOW" : "ABOVE") : "—";
    };
    const styleVerdict = (d, col) => {
      if (d.section !== "body" || d.column.index !== col) return;
      if (d.cell.raw === "In range") d.cell.styles.textColor = GREEN;
      else if (/^(TOO|BELOW|ABOVE)/.test(d.cell.raw)) { d.cell.styles.textColor = FAIL; d.cell.styles.fontStyle = "bold"; }
    };
    if (pre) {
      // Before / after / change; cycle times judged on the target range, the rest flagged on a >10% change
      const bm = pre.measures || {};
      const fmt = n => (n > 0 ? "+" : "") + (Math.round(n * 10) / 10);
      const body = g.items.map(it => {
        const ch = readingChange(bm[it.key], mv[it.key]);
        const flag = ch && ch.flag && !rangeFor(it.key);
        const change = !ch ? "—" : `${fmt(ch.diff)} ${it.unit}${isFinite(ch.pct) ? ` (${fmt(ch.pct)}%)` : ""}${flag ? "  FLAG" : ""}`;
        const row = [it.label, reading(bm[it.key], it.unit), reading(mv[it.key], it.unit), change];
        return g.targets ? [...row, target(it.key, it.unit), verdict(it.key, it.unit)] : row;
      });
      autoTable(doc, {
        startY:y, head:[g.targets ? ["Test","Before","After","Change","Target","Result"] : ["Test","Before","After","Change"]], body,
        margin:{left:10,right:10},
        headStyles:{fillColor:CYAN,textColor:255,fontSize:8,fontStyle:"bold"},
        bodyStyles:{fontSize:8}, theme:"striped", alternateRowStyles:{fillColor:[248,250,253]},
        columnStyles: g.targets
          ? {1:{cellWidth:20,halign:"right",textColor:[130,130,130]},2:{cellWidth:20,halign:"right"},3:{cellWidth:32,halign:"right"},4:{cellWidth:24,halign:"right"},5:{cellWidth:22,halign:"center"}}
          : {1:{cellWidth:28,halign:"right",textColor:[130,130,130]},2:{cellWidth:28,halign:"right"},3:{cellWidth:44,halign:"right"}},
        didParseCell:d=>{
          if(d.section==="body"&&d.column.index===3&&/FLAG$/.test(d.cell.raw)) { d.cell.styles.textColor=FAIL; d.cell.styles.fontStyle="bold"; }
          if (g.targets) styleVerdict(d, 5);
        },
      });
    } else {
      autoTable(doc, {
        startY:y, head:[g.targets ? ["Test","Reading","Target","Result"] : ["Test","Reading"]],
        body: g.items.map(it => g.targets ? [it.label, reading(mv[it.key], it.unit), target(it.key, it.unit), verdict(it.key, it.unit)] : [it.label, reading(mv[it.key], it.unit)]),
        margin:{left:10,right:10},
        headStyles:{fillColor:CYAN,textColor:255,fontSize:8,fontStyle:"bold"},
        bodyStyles:{fontSize:8}, theme:"striped", alternateRowStyles:{fillColor:[248,250,253]},
        columnStyles: g.targets ? {1:{cellWidth:30,halign:"right"},2:{cellWidth:30,halign:"right"},3:{cellWidth:28,halign:"center"}} : {1:{cellWidth:40,halign:"right"}},
        didParseCell:d=>{ if (g.targets) styleVerdict(d, 3); },
      });
    }
    y = doc.lastAutoTable.finalY + 4;
    const ph = state.measurePhotos?.[g.id] || [];
    if (ph.length) {
      const pw = (W - 32) / 3, phh = pw * 0.75;
      ph.forEach((src, i) => {
        const col = i % 3;
        if (col === 0) addPageIfNeeded(phh + 6);
        addPhoto(src, 10 + col * (pw + 6), y, pw, phh);
        if (col === 2 || i === ph.length - 1) y += phh + 6;
      });
    }
    y += 2;
  });

  // ── Service actions ───────────────────────────────────────────────────────────
  if (config.serviceActions?.length) {
    sectionHeading("Service Actions");
    const sa = state.serviceActions||{};
    const saRows = config.serviceActions.map(item => {
      const status = sa[item.key]==="done"?"✓ Done":sa[item.key]==="na"?"N/A":sa[item.key]==="skip"?"Skip":"—";
      // Litres/quantity is recorded internally for the tech but left off the
      // customer-facing PDF — that level of detail belongs on the quote, not here.
      // Exception: diesel added on off-hire is shown, as it is recharged to the hirer.
      const extra  = item.condition ? (sa[item.condition]||"") : (item.showLitres && sa[item.litres]) ? `${sa[item.litres]} L` : "";
      return [item.customerLabel || item.label, status, extra];
    });
    autoTable(doc, {
      startY:y, head:[["Action","Status","Notes"]], body:saRows,
      margin:{left:10,right:10},
      headStyles:{fillColor:CYAN,textColor:255,fontSize:8},
      bodyStyles:{fontSize:8}, theme:"striped",
      alternateRowStyles:{fillColor:[248,250,253]},
      columnStyles:{1:{cellWidth:22,halign:"center"},2:{cellWidth:38}},
      didParseCell:d=>{
        if(d.section==="body"&&d.column.index===1&&d.cell.raw==="✓ Done")
          d.cell.styles.textColor=GREEN;
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Oil sample photo
    if (state.oilSamplePhoto) {
      addPageIfNeeded(55);
      doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(...BLUE);
      doc.text("OIL SAMPLE — TRACKING LABEL", 12, y+4); y+=8;
      addPhoto(state.oilSamplePhoto, 10, y, 60, 45);
      y+=50;
    }
  }

  // ── Defects ───────────────────────────────────────────────────────────────────
  sectionHeading("Defects & Rectifications", FAIL);
  const defects = (state.defects||[]).filter(d=>d.defect);

  if (!defects.length) {
    doc.setFontSize(8); doc.setTextColor(...GRAY);
    doc.text("No defects recorded.", 12, y+5); y+=12;
  } else {
    defects.forEach((defect, idx) => {
      addPageIfNeeded(20);
      // Defect number badge
      doc.setFillColor(...FAIL);
      doc.roundedRect(10, y, 18, 6, 1, 1, "F");
      doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(255,255,255);
      doc.text(`#${idx+1}`, 14, y+4.5);
      doc.setTextColor(30,30,30);
      doc.setFontSize(9);
      doc.text(defect.defect||"—", 32, y+4.5);
      y+=8;

      if (defect.rect) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECTIFICATION", 12, y+3);
        doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const lines = doc.splitTextToSize(defect.rect, W-24);
        doc.text(lines, 12, y+7);
        y += 6 + (lines.length * 4);
      }

      // Rectification photos
      if (defect.rectPhotos?.length) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECTIFICATION PHOTOS", 12, y+3); y+=6;
        const photoW = (W-28)/2;
        const photoH = photoW * 0.75;
        let col = 0;
        defect.rectPhotos.forEach(src => {
          addPageIfNeeded(photoH+3);
          const px = 10 + col*(photoW+8);
          addPhoto(src, px, y, photoW, photoH);
          col++;
          if(col>=2){ col=0; y+=photoH+3; }
        });
        if(col>0) y+=photoH+3;
      }

      if (defect.recommendation) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECOMMENDATION", 12, y+3);
        doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const lines = doc.splitTextToSize(defect.recommendation, W-24);
        doc.text(lines, 12, y+7);
        y += 6 + (lines.length * 4);
      }

      // Defect photos
      if (defect.photos?.length) {
        const photoW = (W-28)/2;
        const photoH = photoW * 0.75;
        let col = 0;
        defect.photos.forEach(src => {
          addPageIfNeeded(photoH+3);
          const px = 10 + col*(photoW+8);
          addPhoto(src, px, y, photoW, photoH);
          col++;
          if(col>=2){ col=0; y+=photoH+3; }
        });
        if(col>0) y+=photoH+3;
      }
      y+=3;
    });
  }

  // ── Other Notes ───────────────────────────────────────────────────────────────
  const otherNotes = (state.otherNotes||[]).filter(n=>n.text||n.photos?.length>0);
  if (otherNotes.length > 0) {
    sectionHeading("Other Notes");
    const defectCount = (state.defects||[]).filter(d=>d.defect).length;
    otherNotes.forEach((note, idx) => {
      const globalNum = defectCount + idx + 1;
      addPageIfNeeded(20);

      // Number badge
      doc.setFillColor(...BLUE);
      doc.roundedRect(10, y, 18, 6, 1, 1, "F");
      doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(255,255,255);
      doc.text(`#${globalNum}`, 14, y+4.5);
      y += 8;

      if (note.text) {
        doc.setFontSize(9); doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const lines = doc.splitTextToSize(note.text, W-24);
        addPageIfNeeded(lines.length*5+4);
        doc.text(lines, 12, y+3);
        y += lines.length*5+4;
      }

      if (note.photos?.length) {
        const photoW = (W-28)/2;
        const photoH = photoW*0.75;
        let col=0, rowY=y;
        note.photos.forEach((src,pi) => {
          if(col===0){ addPageIfNeeded(photoH+3); rowY=y; y+=photoH+3; }
          const px=10+col*(photoW+8);
          addPhoto(src, px, rowY, photoW, photoH);
          col = col===0?1:0;
        });
        y+=2;
      }

      if (note.rect) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECTIFICATION", 12, y+3);
        doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const lines = doc.splitTextToSize(note.rect, W-24);
        doc.text(lines, 12, y+7);
        y += 6 + (lines.length * 4);
      }

      if (note.rectPhotos?.length) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECTIFICATION PHOTOS", 12, y+3); y+=6;
        const photoW = (W-28)/2;
        const photoH = photoW*0.75;
        let col=0, rowY=y;
        note.rectPhotos.forEach(src => {
          if(col===0){ addPageIfNeeded(photoH+3); rowY=y; y+=photoH+3; }
          const px=10+col*(photoW+8);
          addPhoto(src, px, rowY, photoW, photoH);
          col = col===0?1:0;
        });
        y+=2;
      }

      if (note.recommendation) {
        doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(...GRAY);
        doc.text("RECOMMENDATION", 12, y+3);
        doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(30,30,30);
        const lines = doc.splitTextToSize(note.recommendation, W-24);
        doc.text(lines, 12, y+7);
        y += 6 + (lines.length * 4);
      }

      y+=3;
    });
  }

  // ── Signature ─────────────────────────────────────────────────────────────────
  if (state.signature) {
    addPageIfNeeded(40);
    sectionHeading("Technician Signature");
    try {
      doc.addImage(state.signature, "PNG", 10, y, 80, 25);
      y += 28;
    } catch(e) {}
    doc.setDrawColor(...BLUE);
    doc.line(10, y, 90, y);
    doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(...GRAY);
    doc.text(header.tech || "Technician", 10, y+5);
    doc.text(header.date || today, 90, y+5, { align:"right" });
    y += 12;
  }

  // ── Team leader sign-off (pre-rebuild) ────────────────────────────────────────
  if (config.teamLeaderSignoff && state.tlSignature) {
    addPageIfNeeded(40);
    sectionHeading("Team Leader Sign Off");
    try { doc.addImage(state.tlSignature, "PNG", 10, y, 80, 25); y += 28; } catch(e) {}
    doc.setDrawColor(...BLUE); doc.line(10, y, 90, y);
    doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(...GRAY);
    doc.text(header.tlName || "Team leader", 10, y+5);
    y += 12;
  }

  // ── Footer ────────────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for(let p=1;p<=totalPages;p++){
    doc.setPage(p);
    doc.setFillColor(...BLUE);
    doc.rect(0,289,W,8,"F");
    doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(200,220,255);
    doc.text(`Waimea Group · ${serviceLabel(config)} · ${config.equipment}${config.draft?" (DRAFT)":""} · Rego ${header.rego||"—"} · Job ${header.jobno||"—"}`,12,294);
    doc.text(`Page ${p} of ${totalPages}`,W-12,294,{align:"right"});
  }

  // ── Filename ──────────────────────────────────────────────────────────────────
  const clean = s=>(s||"").replace(/[^a-zA-Z0-9]/g,"").toUpperCase();
  const filename = `${clean(header.jobno)||"J000"}-${clean(header.model||header.tankerModel||config.equipment)}-${clean(header.rego)||"REGO"}-${serviceLabel(config)}${config.draft?"-DRAFT":""}.pdf`;
  // Carry the defects/notes/photos invisibly so the Workshop Quotes app can
  // build a quote straight from this PDF once it is filed in WFM job docs.
  try { embedQuoteData(doc, config, header, state); } catch (e) { /* PDF still saves without it */ }
  doc.save(filename);
  return filename;
}
